import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Upload, FileText, CheckCircle2, AlertCircle, History, FileSpreadsheet, Zap } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AdminLayout } from "./AdminLayout";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUpload() {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadLogs, setUploadLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [uploadMode, setUploadMode] = useState("csv"); // "pdf" or "csv"

  const allowedTypes = uploadMode === "csv" 
    ? ["text/csv", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel"]
    : ["application/pdf"];
  
  const allowedExtensions = uploadMode === "csv"
    ? [".csv", ".xlsx", ".xls"]
    : [".pdf"];

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragging(true);
    } else if (e.type === "dragleave") {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const ext = "." + file.name.split('.').pop().toLowerCase();
      if (allowedExtensions.includes(ext)) {
        setSelectedFile(file);
        setUploadResult(null);
      } else {
        toast.error(`Hanya file ${allowedExtensions.join(", ")} yang diizinkan`);
      }
    }
  }, [allowedExtensions]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = "." + file.name.split('.').pop().toLowerCase();
      if (allowedExtensions.includes(ext)) {
        setSelectedFile(file);
        setUploadResult(null);
      } else {
        toast.error(`Hanya file ${allowedExtensions.join(", ")} yang diizinkan`);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    const fileSizeMB = selectedFile.size / (1024 * 1024);
    
    // Determine endpoint based on mode
    const endpoint = uploadMode === "csv" ? `${API}/admin/upload-csv` : `${API}/admin/upload-pdf`;
    
    if (uploadMode === "pdf" && fileSizeMB > 3) {
      toast.warning(`File PDF ${fileSizeMB.toFixed(1)} MB cukup besar. Disarankan pakai CSV/Excel untuk file besar!`, {
        duration: 5000
      });
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append("file", selectedFile);

      const token = localStorage.getItem("admin_token");
      const response = await axios.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        timeout: 180000, // 3 menit timeout
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          if (percentCompleted === 100) {
            toast.info("File terupload, sedang memproses data...", { duration: 15000 });
          }
        }
      });

      setUploadResult(response.data);
      toast.success(`${response.data.total_records} data berhasil diproses!`);
      setSelectedFile(null);
    } catch (err) {
      let errorMsg = "Upload gagal";
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        errorMsg = uploadMode === "pdf" 
          ? "Timeout! Gunakan CSV/Excel untuk file besar, jauh lebih cepat!" 
          : "Timeout! Coba pecah file jadi lebih kecil";
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      toast.error(errorMsg);
      setUploadResult({ error: errorMsg });
    } finally {
      setIsUploading(false);
    }
  };

  const fetchUploadLogs = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/upload-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUploadLogs(response.data);
      setShowLogs(true);
    } catch (err) {
      toast.error("Gagal memuat log upload");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <AdminLayout title="Upload Data Tagihan">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="max-w-2xl mx-auto"
      >
        {/* Mode Selection */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <p className="text-sm font-medium text-slate-600 mb-3">Pilih Metode Upload:</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => { setUploadMode("csv"); setSelectedFile(null); setUploadResult(null); }}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  uploadMode === "csv" 
                    ? "border-emerald-500 bg-emerald-50" 
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    uploadMode === "csv" ? "bg-emerald-500" : "bg-slate-200"
                  }`}>
                    <FileSpreadsheet className={`w-5 h-5 ${uploadMode === "csv" ? "text-white" : "text-slate-500"}`} />
                  </div>
                  <div>
                    <p className={`font-semibold ${uploadMode === "csv" ? "text-emerald-700" : "text-slate-700"}`}>
                      CSV / Excel
                    </p>
                    <div className="flex items-center gap-1">
                      <Zap className="w-3 h-3 text-amber-500" />
                      <span className="text-xs text-amber-600 font-medium">LEBIH CEPAT!</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-500">File besar tanpa timeout</p>
              </button>
              
              <button
                onClick={() => { setUploadMode("pdf"); setSelectedFile(null); setUploadResult(null); }}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  uploadMode === "pdf" 
                    ? "border-sky-500 bg-sky-50" 
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    uploadMode === "pdf" ? "bg-sky-500" : "bg-slate-200"
                  }`}>
                    <FileText className={`w-5 h-5 ${uploadMode === "pdf" ? "text-white" : "text-slate-500"}`} />
                  </div>
                  <div>
                    <p className={`font-semibold ${uploadMode === "pdf" ? "text-sky-700" : "text-slate-700"}`}>
                      PDF
                    </p>
                    <span className="text-xs text-slate-400">Maks ~2MB</span>
                  </div>
                </div>
                <p className="text-xs text-slate-500">File kecil saja</p>
              </button>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload File {uploadMode === "csv" ? "CSV/Excel" : "PDF"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* CSV Format Info */}
            {uploadMode === "csv" && (
              <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                <p className="text-sm font-medium text-emerald-800 mb-2">Format CSV/Excel yang didukung:</p>
                <p className="text-xs text-emerald-700">
                  Kolom wajib: <code className="bg-emerald-100 px-1 rounded">no_samb</code>, <code className="bg-emerald-100 px-1 rounded">nama</code>, <code className="bg-emerald-100 px-1 rounded">bulan</code>, <code className="bg-emerald-100 px-1 rounded">jumlah</code>
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  Kolom opsional: alamat, gol, m3, harga_air, adm, denda
                </p>
              </div>
            )}

            {/* Dropzone */}
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`dropzone ${isDragging ? "active" : ""}`}
            >
              <input
                type="file"
                accept={uploadMode === "csv" ? ".csv,.xlsx,.xls" : ".pdf"}
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
                data-testid="file-input"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <div className="flex flex-col items-center">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${
                    uploadMode === "csv" ? "bg-emerald-100" : "bg-sky-100"
                  }`}>
                    {uploadMode === "csv" 
                      ? <FileSpreadsheet className="w-8 h-8 text-emerald-600" />
                      : <FileText className="w-8 h-8 text-sky-600" />
                    }
                  </div>
                  <p className="text-lg font-medium text-slate-800 mb-2">
                    {selectedFile ? selectedFile.name : `Drag & drop file ${uploadMode === "csv" ? "CSV/Excel" : "PDF"} di sini`}
                  </p>
                  <p className="text-sm text-slate-500">
                    atau klik untuk memilih file ({uploadMode === "csv" ? ".csv, .xlsx" : ".pdf"})
                  </p>
                </div>
              </label>
            </div>

            {selectedFile && (
              <div className={`mt-6 flex items-center justify-between p-4 rounded-xl ${
                uploadMode === "csv" ? "bg-emerald-50" : "bg-sky-50"
              }`}>
                <div className="flex items-center gap-3">
                  {uploadMode === "csv" 
                    ? <FileSpreadsheet className="w-8 h-8 text-emerald-600" />
                    : <FileText className="w-8 h-8 text-sky-600" />
                  }
                  <div>
                    <p className="font-medium text-slate-800">{selectedFile.name}</p>
                    <p className="text-sm text-slate-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleUpload}
                  disabled={isUploading}
                  className={uploadMode === "csv" ? "bg-emerald-600 hover:bg-emerald-700" : "btn-flow"}
                  data-testid="upload-btn"
                >
                  {isUploading ? <div className="spinner w-5 h-5" /> : `Proses ${uploadMode === "csv" ? "Data" : "PDF"}`}
                </Button>
              </div>
            )}

            {/* Upload Result */}
            {uploadResult && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6"
              >
                {uploadResult.error ? (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-6 h-6 text-red-600" />
                      <div>
                        <p className="font-medium text-red-800">Upload Gagal</p>
                        <p className="text-sm text-red-600">{uploadResult.error}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <div className="flex items-center gap-3 mb-3">
                      <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                      <p className="font-medium text-emerald-800">PDF Berhasil Diproses!</p>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-sky-600">{uploadResult.total_records}</p>
                        <p className="text-xs text-slate-500">Total Data</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-emerald-600">{uploadResult.inserted}</p>
                        <p className="text-xs text-slate-500">Baru</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-amber-600">{uploadResult.updated}</p>
                        <p className="text-xs text-slate-500">Diperbarui</p>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </CardContent>
        </Card>

        {/* Upload History */}
        <Button
          variant="outline"
          onClick={fetchUploadLogs}
          className="w-full gap-2"
        >
          <History className="w-4 h-4" />
          Lihat Riwayat Upload
        </Button>

        {showLogs && uploadLogs.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="mt-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-heading">Riwayat Upload</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {uploadLogs.map((log) => (
                    <div key={log.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-800">{log.filename}</p>
                        <p className="text-xs text-slate-500">
                          Oleh {log.uploaded_by} • {formatDate(log.uploaded_at)}
                        </p>
                      </div>
                      <div className="text-right text-sm">
                        <p className="text-slate-600">{log.total_records} records</p>
                        <p className="text-xs text-slate-500">
                          {log.bills_inserted} baru, {log.bills_updated} update
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </motion.div>
    </AdminLayout>
  );
}
